import { useTranslation } from '@doccareplus/i18n';
import { detailSlots, itemDetailValues, resolveHeading, sectionLabel, sectionNumbers } from './format';
import { PaperSection, PrescriptionPaper } from './PrescriptionPaper';
import type {
  PrescriptionBrandingResponse,
  PrescriptionDocumentData,
  PrescriptionDocumentItem,
  PrescriptionFormatResponse,
  PrescriptionSectionKey,
  PrescriptionPageSize,
  PrintMode,
} from './types';

const SECTION_ORDER: PrescriptionSectionKey[] = [
  'Vitals',
  'Complaint',
  'Diagnosis',
  'Investigations',
  'Prescription',
  'Advice',
  'FollowUp',
];

export function PrescriptionDocument({
  branding,
  format,
  document,
  printMode,
  pageSize,
  clinicFallbackName,
}: {
  branding: PrescriptionBrandingResponse | undefined;
  format: PrescriptionFormatResponse | undefined;
  document: PrescriptionDocumentData;
  printMode?: PrintMode;
  pageSize?: PrescriptionPageSize;
  /** Older snapshots may lack resolved layout branding; retain their issuing-clinic identity. */
  clinicFallbackName?: string | null;
}) {
  const { t } = useTranslation('appointments');
  const accent = branding?.accentColor;
  const numbers = sectionNumbers(format, SECTION_ORDER);
  const label = (key: PrescriptionSectionKey, fallback: string) =>
    sectionLabel(format, key, t, fallback);
  const slots = detailSlots(format);
  // oxlint-disable-next-line unicorn/no-array-sort -- sort a copy because the current TS target has no toSorted.
  const items = [...document.items].sort((a, b) => a.order - b.order);
  const dash = t('visit.details.empty');

  const labels = {
    Vitals: label('Vitals', t('visit.details.vitals')),
    Complaint: label('Complaint', t('visit.details.complaint')),
    Diagnosis: label('Diagnosis', t('visit.details.diagnosis')),
    Investigations: label('Investigations', t('visit.details.investigations')),
    Prescription: label('Prescription', t('visit.details.prescription')),
    Advice: label('Advice', t('visit.details.advice')),
    FollowUp: label('FollowUp', t('visit.details.followUp')),
  };

  return (
    <PrescriptionPaper
      branding={branding}
      printMode={printMode}
      pageSize={pageSize}
      clinicFallbackName={clinicFallbackName}
      paged
      providerName={document.provider.name}
      providerQualification={document.provider.qualification}
      providerRegNo={document.provider.registrationNumber}
      patient={document.patient}
      visitDate={document.visitDate}
      allergy={document.allergy}
      shareUrl={document.shareUrl}
    >
      {labels.Vitals && document.vitals.length > 0 && (
        <PaperSection number={numbers.Vitals} title={labels.Vitals} accentColor={accent}>
          <div className="flex flex-wrap gap-2">
            {document.vitals.map((vital) => (
              <span key={vital} className="rounded-[7px] border border-border bg-surface-2 px-2.5 py-1.5 text-13 font-semibold text-text">
                {vital}
              </span>
            ))}
          </div>
        </PaperSection>
      )}

      {labels.Complaint && (
        <PaperSection number={numbers.Complaint} title={labels.Complaint} accentColor={accent}>
          <p className="whitespace-pre-line text-14 leading-relaxed text-text">{document.complaint || dash}</p>
        </PaperSection>
      )}

      {labels.Diagnosis && (
        <PaperSection number={numbers.Diagnosis} title={labels.Diagnosis} accentColor={accent}>
          <p className="text-15 font-semibold text-text">{document.diagnosis || dash}</p>
        </PaperSection>
      )}

      {labels.Investigations && document.investigations.length > 0 && (
        <PaperSection number={numbers.Investigations} title={labels.Investigations} accentColor={accent}>
          <ol className="list-decimal space-y-1 pl-5 text-14 text-text">
            {document.investigations.map((testName) => <li key={testName}>{testName}</li>)}
          </ol>
        </PaperSection>
      )}

      {labels.Prescription && (
        <PaperSection number={numbers.Prescription} title={labels.Prescription} accentColor={accent} allowBreak>
          <div className="flex gap-3.5">
            <span aria-hidden className="w-6 flex-none font-mono text-[32px] font-semibold leading-none" style={{ color: accent ?? 'var(--primary)' }}>
              {resolveHeading(format, t) || '℞'}
            </span>
            {items.length === 0 ? (
              <p className="text-13 text-text-3">{t('visit.details.noMedicines')}</p>
            ) : (
              <ol data-testid="rx-items" className="flex-1">
                {items.map((item, index) => (
                  <li key={`${item.order}-${item.medicineName}`} className="rx-no-break border-b border-dashed border-border py-2 last:border-b-0">
                    <div className="text-14 font-semibold text-text">
                      <span aria-hidden>{index + 1}. </span>
                      <span>{item.medicineName}</span>
                    </div>
                    {detailLine(item, slots) && <div className="text-12 text-text-2">{detailLine(item, slots)}</div>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </PaperSection>
      )}

      {labels.Advice && (
        <PaperSection number={numbers.Advice} title={labels.Advice} accentColor={accent}>
          <p className="whitespace-pre-line text-14 leading-relaxed text-text">{document.advice || dash}</p>
        </PaperSection>
      )}

      <div className="rx-no-break mt-2 flex flex-wrap items-end justify-between gap-5 border-t border-border pt-4">
        {labels.FollowUp ? (
          <div className="text-13 text-text-2">
            <div className="mb-0.5 text-11 font-bold uppercase tracking-wide text-text-3">{labels.FollowUp}</div>
            {document.followUpDate ? new Date(document.followUpDate).toLocaleDateString() : dash}
          </div>
        ) : <div />}
        <div className="min-w-[170px] text-center">
          <div className="border-t border-text pt-1.5 text-12 font-bold text-text">
            {document.signedByName || document.provider.name}
          </div>
          <div className="text-11 text-text-3">{t('visit.details.signature')}</div>
        </div>
      </div>
    </PrescriptionPaper>
  );
}

function detailLine(
  item: PrescriptionDocumentItem,
  slots: PrescriptionFormatResponse['slots']
): string {
  const values = itemDetailValues(item, slots);
  if (values.length === 0) return '';
  const showsInstructions = slots.some((slot) => slot.key === 'Instructions');
  if (showsInstructions && item.instructions?.trim() && values.length > 1) {
    return `${values.slice(0, -1).join(' · ')} — ${values[values.length - 1]}`;
  }
  return values.join(' · ');
}
